<template>
  <div
    :class="[
      'HandshakeButton',
      {
        'is-badge': isBadge,
        'is-completed': isCompleted,
        'is-recommendation': isRecommendation && !isLit,
        'is-unplayed': isRecommendation && isLit,
        'is-pending': isPending,
        'is-waiting': isWaiting,
        'has-cta-label': (!!ctaLabel || isRecommendation) && !isBadge,
      },
    ]"
    :aria-label="label"
    role="button"
    @click="onClick"
  >
    <span v-if="isBadge" class="badge-mark">
      <!-- A drag spins the glass and must not open the collection. A tap still does. -->
      <HandshakeMetric
        class="badge-sphere"
        spin
        :icon="isMaster ? GamIconName.CROWN : GamIconName.HANDSHAKE_EMPTY"
        :label="!isMaster && pairCount != null ? String(pairCount) : undefined"
        @click.stop
        @tap="onClick"
      />
    </span>
    <!--
      Always in words, not only while learning: which of the three it is decides
      what the tap does, and the number alone does not say.
    -->
    <template v-else-if="isRecommendation">
      <span v-if="position == null" class="cta-label">{{ localize('handshake.cta.new960') }}</span>
      <span v-else-if="isLit" class="cta-label">{{
        localizeWithValues('handshake.cta.playNumbered', [String(position)])
      }}</span>
      <template v-else>
        <GamIcon class="icon" :name="GamIconName.CHESS_CLOCK" size="16px" is-current />
        <span class="position-label">#{{ position }}</span>
      </template>
    </template>
    <!-- A tap that only toasts. The hand is filled because the handshake already happened. -->
    <span v-else-if="isCompleted">
      <GamIcon class="icon handshake" :name="GamIconName.HANDSHAKE" size="22px" is-current />
    </span>
    <!-- An open hand for a handshake still to be made, a shaken one for a
         handshake that has been: both at full strength, so neither reads as a
         button that cannot be pressed. -->
    <span v-else-if="!isPending || isWaiting">
      <GamIcon class="icon handshake" :name="GamIconName.HANDSHAKE_EMPTY" size="20px" />
    </span>

    <span v-else-if="isPending">
      <GamIcon class="icon" :name="GamIconName.TIME" size="20px" />
    </span>

    <span v-if="ctaLabel && !isBadge && !isRecommendation" class="cta-label">{{ ctaLabel }}</span>
  </div>
</template>

<script setup lang="ts">
import { localize, localizeWithValues } from '@/core/gambits';
import { useAuthStore } from '@/stores/auth.store';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import HandshakeMetric from '@/views/main/handshakes/HandshakeMetric.vue';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

export interface HandshakeButtonProps {
  onClick: () => void;
  responderId: string | null;
  isCompleted: boolean;
  isPending: boolean;
  /** This tap opens the game, so the mark is the recommendation. */
  isRecommendation?: boolean;
  /** No position played on this handshake yet, so the clock is pink and asks for a game. */
  isUnplayed?: boolean;
  /** The recommendation's Chess960 number, named in place of the clock until it is saved. */
  position?: number | null;
  /** Play started that position and Done is the question now, so the number goes gray. */
  positionOn?: boolean;
  /** Profile pendant: a glass sphere. A pair count, or a crown at master, sits in the glass. */
  isBadge?: boolean;
  /** Unique pairs, shown in the glass. */
  pairCount?: number;
  /** The collection is complete, so the glass holds a crown. */
  isMaster?: boolean;
  /** Named for someone who cannot see the icon. */
  label?: string;
  /** Words beside the icon, for a player who has not learned what it does yet. */
  ctaLabel?: string;
}

const props = defineProps<HandshakeButtonProps>();

const authStore = useAuthStore();
const { user } = storeToRefs(authStore);

/** A number still to be played is pink, and gray once a game is on it. Without one, pink until a game is saved. */
const isLit = computed(() => (props.position != null ? !props.positionOn : !!props.isUnplayed));

const isWaiting = computed(() => {
  return props.isPending && props.responderId === user.value?.id;
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
@use '@/ui/css/partial';

/* The shadow of `raised-action`, for the breathing disc that animates its own fill. */
$raised-shadow:
  inset 0 1px 0 var(--color-white-20),
  inset 0 -2px 4px rgba(0, 0, 0, 0.25),
  0 4px 10px rgba(0, 0, 0, 0.55);

@keyframes breathe {
  0% {
    background-color: var(--color-pink-30);
    border-color: var(--color-pink);
  }

  100% {
    background-color: var(--color-white-10);
    border-color: var(--color-pink-30);
  }
}

.HandshakeButton {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 28px;
  color: var(--color-white-40);
  cursor: pointer;

  @extend %tinted-card;

  // The next step with this person. It follows the extend so the fabric fill
  // does not cover it.
  @include raised-action;

  &.selected {
    border-color: var(--color-highlight);
    @extend %active-tinted-card;
  }

  @include press;
}

.HandshakeButton.is-badge {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: transparent;
  box-shadow: none;
  color: var(--color-white);

  @include hover {
    background: transparent;
    box-shadow: none;
  }
}

.badge-mark {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

// The glass is the whole pendant, so the button is the sphere.
.badge-sphere {
  --sphere-size: 44px;
  position: relative;
  z-index: 1;
}

// Sent and not answered yet: still the handshake, just not waiting on you.
.HandshakeButton.is-pending:not(.is-waiting) {
  border: 0;
  @include raised-action(gray);
}

.HandshakeButton.is-waiting {
  background: var(--color-pink-30);
  border: 2px solid var(--color-pink);
  color: var(--color-white);
  // Their handshake is waiting on you, so it is still the next step.
  box-shadow: $raised-shadow;

  animation: breathe 4s ease-in-out alternate infinite;
}

.HandshakeButton.is-completed {
  background: transparent;
  border: 2px solid var(--color-white-20);
  box-shadow: none;
  color: var(--color-white);

  @include hover {
    background: transparent;
    box-shadow: none;
  }
}

// A position still to be played, or the clock before any game: the pink disc, the next step with this person.
.HandshakeButton.is-unplayed {
  color: var(--color-white);
}

// The clock once a game is played: gray beside the pink handshake, and still a button.
.HandshakeButton.is-recommendation {
  border: 0;
  color: var(--color-white-70);
  @include raised-action(gray);

  @include hover {
    color: var(--color-white);
  }

  .cta-label {
    color: inherit;
  }
}

.HandshakeButton.has-cta-label {
  width: auto;
  gap: 6px;
  // Even on both sides, so the icon and the words together sit in the middle of the pill.
  padding: 0 var(--spacing-l);
}

.cta-label {
  color: var(--color-white);
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

/* The position Done is waiting on, in place of the clock. */
.position-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0;
  @include disc-text;
}
</style>
